/**
 * VRG-721 — what the support intake is handed when it opens: the error a toast
 * showed, and the user's unsent draft.
 *
 * Both live in sessionStorage. Router state was the alternative for the error,
 * and it loses twice: `SnackbarProvider` sits OUTSIDE `BrowserRouter` (it cannot
 * navigate with state, only `pushState`), and a cmd-click "open in new tab" on
 * the link carries no router state at all — while same-origin sessionStorage is
 * copied into a tab opened from a link. The URL carries only an opaque handoff id
 * (`?error=<id>`), never the error itself, so nothing about the failure lands in
 * browser history, a referrer or a pasted link.
 *
 * Every accessor takes the storage explicitly and swallows storage failures
 * (private mode, quota): a draft that could not be saved is a lost convenience,
 * and must never become a broken form. ZERO app imports, so it is unit-tested
 * under node (`test/support-error-report.guard.spec.ts`).
 */
import type { ToastErrorContext } from './errorContext'

export interface KeyValueStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

const safeStorage = (): KeyValueStorage | null => {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

// ── Error handoff ─────────────────────────────────────────────────────────────

export const ERROR_HANDOFF_KEY = 'vera:supportErrorHandoff'
/** Long enough to read a toast, decide, and click; short enough that a stale id resolves to nothing. */
export const ERROR_HANDOFF_TTL_MS = 30 * 60 * 1000
const MAX_HANDOFFS = 5

export interface ErrorHandoff {
  id: string
  error: ToastErrorContext
  /** The signature key the quiet automatic report was filed under, if one was. */
  signatureKey: string | null
  savedAt: number
}

const readAll = (storage: KeyValueStorage): ErrorHandoff[] => {
  try {
    const raw = storage.getItem(ERROR_HANDOFF_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((h) => h && typeof h.id === 'string' && h.error) : []
  } catch {
    return []
  }
}

/** Store the toast's error under a fresh id; returns the id for the link's `?error=`. */
export const writeErrorHandoff = (
  error: ToastErrorContext,
  signatureKey: string | null,
  storage: KeyValueStorage | null = safeStorage(),
  now: number = Date.now(),
): string | null => {
  if (!storage) return null
  try {
    const id = `${now.toString(36)}${Math.random().toString(36).slice(2, 8)}`
    const kept = readAll(storage).filter((h) => now - h.savedAt <= ERROR_HANDOFF_TTL_MS)
    const next = [...kept, { id, error, signatureKey, savedAt: now }].slice(-MAX_HANDOFFS)
    storage.setItem(ERROR_HANDOFF_KEY, JSON.stringify(next))
    return id
  } catch {
    return null
  }
}

export const readErrorHandoff = (
  id: string | null | undefined,
  storage: KeyValueStorage | null = safeStorage(),
  now: number = Date.now(),
): ErrorHandoff | null => {
  if (!id || !storage) return null
  const found = readAll(storage).find((h) => h.id === id)
  if (!found || now - found.savedAt > ERROR_HANDOFF_TTL_MS) return null
  return found
}

export const clearErrorHandoff = (id: string | null | undefined, storage: KeyValueStorage | null = safeStorage()) => {
  if (!id || !storage) return
  try {
    storage.setItem(ERROR_HANDOFF_KEY, JSON.stringify(readAll(storage).filter((h) => h.id !== id)))
  } catch {
    /* nothing to do */
  }
}

// ── Drafts ────────────────────────────────────────────────────────────────────

export const DRAFT_KEY_PREFIX = 'vera:supportDraft:'

export interface SupportDraft {
  type: 'support' | 'bug' | 'feature'
  body: string
}

/**
 * Keyed by the BACKGROUND route's pathname — the page the request is about — so a
 * half-written bug report about the products page comes back on the products page
 * and not on the billing page. Query string excluded: a filter does not make it a
 * different page, and the query is where search terms and emails live.
 */
export const draftKeyFor = (backgroundPath: string | null | undefined): string =>
  `${DRAFT_KEY_PREFIX}${(backgroundPath ?? '').split('?')[0].split('#')[0] || '/'}`

export const readDraft = (key: string, storage: KeyValueStorage | null = safeStorage()): SupportDraft | null => {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const type = parsed?.type
    if (type !== 'support' && type !== 'bug' && type !== 'feature') return null
    return { type, body: typeof parsed.body === 'string' ? parsed.body : '' }
  } catch {
    return null
  }
}

/** An empty body REMOVES the draft: nothing typed is nothing to restore. */
export const saveDraft = (key: string, draft: SupportDraft, storage: KeyValueStorage | null = safeStorage()) => {
  if (!storage) return
  try {
    if (!draft.body.trim()) storage.removeItem(key)
    else storage.setItem(key, JSON.stringify({ type: draft.type, body: draft.body.slice(0, 20000) }))
  } catch {
    /* quota or private mode — the form still works, it just won't remember */
  }
}

export const clearDraft = (key: string, storage: KeyValueStorage | null = safeStorage()) => {
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch {
    /* nothing to do */
  }
}

// ── The line under the form ───────────────────────────────────────────────────

/**
 * What the form says it attaches. Pure, so the copy is pinned by a test.
 *
 * Replaced "…so there is nothing else to fill in": there IS something to fill in
 * — what happened — and a reassurance that contradicts the empty text box above
 * it reads as boilerplate. Each variant names only what is actually attached.
 */
export const attachedContextCopy = (params: { hasError: boolean; hasPage: boolean }): string => {
  if (params.hasError) {
    return "We'll attach the error you just saw, plus the page, your browser, app version and account."
  }
  if (params.hasPage) return "We'll attach the page you came from, your browser, app version and account."
  return "We'll attach your browser, app version and account."
}
